import type { Language } from "~/utils/language";
import {
  API_RATE_LIMIT_DAILY,
  API_RATE_LIMIT_HOURLY,
} from "~/utils/apiRateLimit";

export type ApiEndpointKind = "assetWorth" | "assets" | "transactions" | "insurance";

export type ApiFieldSpec = {
  name: string;
  type: string;
  required?: boolean;
  description: Record<Language, string>;
};

export type ApiQueryParamSpec = ApiFieldSpec & {
  defaultValue?: string;
};

export type ApiEndpointDoc = {
  kind: ApiEndpointKind;
  method: "GET";
  path: string;
  summary: Record<Language, string>;
  description: Record<Language, string>;
  useWhen: Record<Language, string>;
  queryParams: ApiQueryParamSpec[];
  responseFields: ApiFieldSpec[];
  exampleResponse: unknown;
};

export type ApiAssetNestedFieldGroup = {
  name: string;
  type: string;
  description: Record<Language, string>;
  fields: ApiFieldSpec[];
};

export type ApiAssetTypeDoc = {
  form: string;
  title: Record<Language, string>;
  description: Record<Language, string>;
  fields: ApiFieldSpec[];
  nestedFieldGroups?: ApiAssetNestedFieldGroup[];
};

export const API_BASE_URL = "https://app.moneytor.co.il";
export const API_ISSUER = "moneytor-api";
export const API_AUDIENCE = "moneytor-api-v1";
export const API_TOKEN_TTL_MONTHS = 6;
export const API_TOKEN_TTL_DAYS = API_TOKEN_TTL_MONTHS * 30;
export const API_TOKEN_TTL_SECONDS = API_TOKEN_TTL_DAYS * 24 * 60 * 60;

export const apiDocsCopy: Record<
  Language,
  {
    productName: string;
    eyebrow: string;
    title: string;
    searchPlaceholder: string;
    nav: {
      overview: string;
      authentication: string;
      simulator: string;
      reference: string;
      errors: string;
      llm: string;
    };
    quickStart: string;
    overviewTitle: string;
    overviewBody: string;
    authTitle: string;
    authBody: string;
    jwtBody: string;
    tokenLifetimeTitle: string;
    tokenLifetimeBody: string;
    rateLimitTitle: string;
    rateLimitBody: string;
    simulatorTitle: string;
    simulatorBody: string;
    referenceTitle: string;
    errorsTitle: string;
    llmTitle: string;
    llmBody: string;
    method: string;
    path: string;
    queryParams: string;
    responseFields: string;
    assetTypesTitle: string;
    commonAssetFieldsTitle: string;
    formSpecificFieldsTitle: string;
    nestedFieldsTitle: string;
    exampleResponse: string;
    noQueryParams: string;
    type: string;
    required: string;
    optional: string;
    defaultValue: string;
    useWhen: string;
    copy: string;
    copied: string;
    apiTokenLabel: string;
    apiTokenPlaceholder: string;
    endpointLabel: string;
    requestPreview: string;
    runRequest: string;
    running: string;
    response: string;
    statusCode: string;
    tokenRequired: string;
    fromDate: string;
    toDate: string;
    limit: string;
    offset: string;
    securityNote: string;
    settingsCta: string;
    backToApp: string;
  }
> = {
  HEB: {
    productName: "Moneytor API",
    eyebrow: "תיעוד API ציבורי",
    title: "בנו אינטגרציות פיננסיות על הדאטה של Moneytor",
    searchPlaceholder: "חיפוש בתיעוד...",
    nav: {
      overview: "סקירה",
      authentication: "אימות",
      simulator: "סימולטור",
      reference: "API Reference",
      errors: "שגיאות",
      llm: "LLM",
    },
    quickStart: "התחלה מהירה",
    overviewTitle: "איך ה-API עובד",
    overviewBody:
      "ה-API הוא לקריאה בלבד ומחזיר שווי נכסים, רשימת נכסים, תנועות תזרים ופוליסות ביטוח (הר הביטוח). כל בקשה מתבצעת בשם המשתמש שמופיע בטוקן.",
    authTitle: "אימות",
    authBody:
      "שלחו JWT API Token בכותרת Authorization. משתמש Premium ומשתמשי מתכנן יוצרים טוקן מאזור ההגדרות של ה-API.",
    jwtBody:
      "הטוקן הוא JWT חתום שמכיל user_id,‏ jti ו-exp. השרת מאמת חתימה, בודק שהתוקף לא פג ושהטוקן לא בוטל, ואז מפעיל מגבלות שימוש.",
    tokenLifetimeTitle: "תוקף הטוקן",
    tokenLifetimeBody:
      `כל טוקן תקף ל-${API_TOKEN_TTL_MONTHS} חודשים מרגע היצירה. כשהתוקף פג, הבקשות נכשלות עם 401 וקוד api_token_expired. צרו טוקן חדש באזור ההגדרות לפני תאריך התפוגה כדי שהאינטגרציות ימשיכו לעבוד.`,
    rateLimitTitle: "מגבלות שימוש",
    rateLimitBody: `${API_RATE_LIMIT_HOURLY} בקשות לשעה ו-${API_RATE_LIMIT_DAILY} בקשות ליום לכל משתמש, לפי UTC.`,
    simulatorTitle: "סימולטור חי",
    simulatorBody:
      "הדביקו JWT API Token ובחרו endpoint כדי להריץ בקשה אמיתית מול ה-API. הטוקן נשמר בזיכרון הדפדפן בלבד.",
    referenceTitle: "API Reference",
    errorsTitle: "שגיאות",
    llmTitle: "ידידותי לסוכני AI",
    llmBody:
      "התיעוד זמין גם כ-Markdown, llms.txt ו-OpenAPI כדי שסוכנים יוכלו להבין את ה-API ללא גירוד HTML.",
    method: "Method",
    path: "Path",
    queryParams: "Query params",
    responseFields: "שדות תגובה",
    assetTypesTitle: "סוגי נכסים ושדות לפי form",
    commonAssetFieldsTitle: "שדות משותפים לכל נכס",
    formSpecificFieldsTitle: "שדות ייחודיים לפי סוג נכס",
    nestedFieldsTitle: "שדות פנימיים",
    exampleResponse: "דוגמת תגובה",
    noQueryParams: "אין פרמטרים",
    type: "סוג",
    required: "חובה",
    optional: "אופציונלי",
    defaultValue: "ברירת מחדל",
    useWhen: "מתי להשתמש",
    copy: "העתקה",
    copied: "הועתק",
    apiTokenLabel: "JWT API Token",
    apiTokenPlaceholder: "הדביקו JWT token",
    endpointLabel: "Endpoint",
    requestPreview: "בקשה",
    runRequest: "הרצת בקשה",
    running: "מריץ...",
    response: "תגובה",
    statusCode: "סטטוס",
    tokenRequired: "יש להזין JWT API Token כדי להריץ בקשה.",
    fromDate: "מתאריך",
    toDate: "עד תאריך",
    limit: "Limit",
    offset: "Offset (לדפדוף)",
    securityNote:
      "טוקן API הוא כמו סיסמה. אל תשלחו אותו לקוד צד לקוח, למאגרים ציבוריים או לכלי שאינכם סומכים עליו.",
    settingsCta: "יצירת טוקן באזור ההגדרות",
    backToApp: "חזרה לאפליקציה",
  },
  ENG: {
    productName: "Moneytor API",
    eyebrow: "Public API documentation",
    title: "Build financial integrations on top of Moneytor data",
    searchPlaceholder: "Search docs...",
    nav: {
      overview: "Overview",
      authentication: "Authentication",
      simulator: "Simulator",
      reference: "API Reference",
      errors: "Errors",
      llm: "LLM",
    },
    quickStart: "Quick start",
    overviewTitle: "How the API works",
    overviewBody:
      "The API is read-only and returns asset worth, enriched assets, cashflow transactions, and Har Bituach insurance policies. Every request runs as the user encoded in the token.",
    authTitle: "Authentication",
    authBody:
      "Send a JWT API token in the Authorization header. Premium and planner users create tokens from the API settings section.",
    jwtBody:
      "The token is a signed JWT containing user_id, jti, and exp. The server verifies the signature, checks expiry and revocation, and then applies usage limits.",
    tokenLifetimeTitle: "Token lifetime",
    tokenLifetimeBody:
      `Each token is valid for ${API_TOKEN_TTL_MONTHS} months from the moment it is created. After that it expires and API requests return 401 with code api_token_expired. Create a new token in Settings → API before the expiry date so existing integrations keep working.`,
    rateLimitTitle: "Rate limits",
    rateLimitBody: `${API_RATE_LIMIT_HOURLY} requests per UTC hour and ${API_RATE_LIMIT_DAILY} requests per UTC day per user.`,
    simulatorTitle: "Live simulator",
    simulatorBody:
      "Paste a JWT API token and choose an endpoint to run a real request against the API. The token stays in browser memory only.",
    referenceTitle: "API Reference",
    errorsTitle: "Errors",
    llmTitle: "AI-agent friendly",
    llmBody:
      "The docs are also available as Markdown, llms.txt, and OpenAPI so agents can understand the API without scraping HTML.",
    method: "Method",
    path: "Path",
    queryParams: "Query params",
    responseFields: "Response fields",
    assetTypesTitle: "Asset types and fields by form",
    commonAssetFieldsTitle: "Common fields on every asset",
    formSpecificFieldsTitle: "Form-specific fields",
    nestedFieldsTitle: "Nested fields",
    exampleResponse: "Example response",
    noQueryParams: "No query params",
    type: "Type",
    required: "Required",
    optional: "Optional",
    defaultValue: "Default",
    useWhen: "Use when",
    copy: "Copy",
    copied: "Copied",
    apiTokenLabel: "JWT API Token",
    apiTokenPlaceholder: "Paste a JWT token",
    endpointLabel: "Endpoint",
    requestPreview: "Request",
    runRequest: "Run request",
    running: "Running...",
    response: "Response",
    statusCode: "Status",
    tokenRequired: "Enter a JWT API token to run a request.",
    fromDate: "From",
    toDate: "To",
    limit: "Limit",
    offset: "Offset (paging)",
    securityNote:
      "An API token is equivalent to a password. Never ship it in frontend code, public repositories, or tools you do not trust.",
    settingsCta: "Create a token in settings",
    backToApp: "Back to app",
  },
  RUS: {
    productName: "Moneytor API",
    eyebrow: "Публичная документация API",
    title: "Создавайте финансовые интеграции на данных Moneytor",
    searchPlaceholder: "Поиск по документации...",
    nav: {
      overview: "Обзор",
      authentication: "Аутентификация",
      simulator: "Симулятор",
      reference: "API Reference",
      errors: "Ошибки",
      llm: "LLM",
    },
    quickStart: "Быстрый старт",
    overviewTitle: "Как работает API",
    overviewBody:
      "API доступен только для чтения и возвращает стоимость активов, список активов, транзакции и полисы страхования Har Bituach. Каждый запрос выполняется от имени пользователя в токене.",
    authTitle: "Аутентификация",
    authBody:
      "Передавайте JWT API Token в заголовке Authorization. Пользователи Premium и планировщики создают токены в настройках API.",
    jwtBody:
      "Токен является подписанным JWT с user_id, jti и exp. Сервер проверяет подпись, срок действия, отзыв токена и лимиты использования.",
    tokenLifetimeTitle: "Срок действия токена",
    tokenLifetimeBody:
      `Каждый токен действует ${API_TOKEN_TTL_MONTHS} месяцев с момента создания. После истечения срока запросы возвращают 401 с кодом api_token_expired. Создайте новый токен в настройках до даты истечения, чтобы интеграции продолжали работать.`,
    rateLimitTitle: "Лимиты",
    rateLimitBody: `${API_RATE_LIMIT_HOURLY} запросов в час UTC и ${API_RATE_LIMIT_DAILY} запросов в день UTC на пользователя.`,
    simulatorTitle: "Живой симулятор",
    simulatorBody:
      "Вставьте JWT API Token и выберите endpoint, чтобы выполнить реальный запрос. Токен хранится только в памяти браузера.",
    referenceTitle: "API Reference",
    errorsTitle: "Ошибки",
    llmTitle: "Для AI-агентов",
    llmBody:
      "Документация доступна как Markdown, llms.txt и OpenAPI, чтобы агенты понимали API без парсинга HTML.",
    method: "Method",
    path: "Path",
    queryParams: "Query params",
    responseFields: "Поля ответа",
    assetTypesTitle: "Типы активов и поля по form",
    commonAssetFieldsTitle: "Общие поля каждого актива",
    formSpecificFieldsTitle: "Поля конкретного типа",
    nestedFieldsTitle: "Вложенные поля",
    exampleResponse: "Пример ответа",
    noQueryParams: "Нет параметров",
    type: "Тип",
    required: "Обязательное",
    optional: "Опциональное",
    defaultValue: "По умолчанию",
    useWhen: "Когда использовать",
    copy: "Копировать",
    copied: "Скопировано",
    apiTokenLabel: "JWT API Token",
    apiTokenPlaceholder: "Вставьте JWT token",
    endpointLabel: "Endpoint",
    requestPreview: "Запрос",
    runRequest: "Выполнить запрос",
    running: "Выполняется...",
    response: "Ответ",
    statusCode: "Статус",
    tokenRequired: "Введите JWT API token, чтобы выполнить запрос.",
    fromDate: "С даты",
    toDate: "По дату",
    limit: "Limit",
    offset: "Offset (пагинация)",
    securityNote:
      "API-токен эквивалентен паролю. Не добавляйте его во frontend-код, публичные репозитории или недоверенные инструменты.",
    settingsCta: "Создать токен в настройках",
    backToApp: "Назад в приложение",
  },
};

const commonDescriptions = {
  ok: {
    HEB: "מציין שהבקשה הצליחה.",
    ENG: "Indicates that the request succeeded.",
    RUS: "Показывает, что запрос выполнен успешно.",
  },
  asOf: {
    HEB: "חותמת זמן ISO-8601 של יצירת התגובה.",
    ENG: "ISO-8601 timestamp when the response was generated.",
    RUS: "Метка времени ISO-8601 создания ответа.",
  },
  baseCurrency: {
    HEB: "מטבע הבסיס של החישובים. כרגע תמיד ILS.",
    ENG: "Base currency for calculations. Currently always ILS.",
    RUS: "Базовая валюта расчетов. Сейчас всегда ILS.",
  },
};

const localized = (
  HEB: string,
  ENG: string,
  RUS: string
): Record<Language, string> => ({ HEB, ENG, RUS });

const field = (
  name: string,
  type: string,
  HEB: string,
  ENG: string,
  RUS: string,
  required = false
): ApiFieldSpec => ({
  name,
  type,
  required,
  description: localized(HEB, ENG, RUS),
});

export const apiCommonAssetFields: ApiFieldSpec[] = [
  field(
    "id",
    "string",
    "מזהה המוצר כמחרוזת.",
    "Product id as a string.",
    "ID продукта в виде строки.",
    true
  ),
  field(
    "productId",
    "number",
    "מזהה המוצר המספרי.",
    "Numeric product id.",
    "Числовой ID продукта.",
    true
  ),
  field(
    "userProductId",
    "number",
    "מזהה הקישור בין המשתמש למוצר.",
    "User-product relation id.",
    "ID связи пользователя и продукта.",
    true
  ),
  field(
    "portfolioProductId",
    "number",
    "מזהה הקישור בין התיק למוצר.",
    "Portfolio-product relation id.",
    "ID связи портфеля и продукта.",
    true
  ),
  field(
    "portfolio_products_id",
    "number",
    "שם שדה מקור לקישור תיק-מוצר.",
    "Source field for the portfolio-product relation.",
    "Исходное поле связи портфеля и продукта."
  ),
  field(
    "user_products_id",
    "number",
    "שם שדה מקור לקישור משתמש-מוצר.",
    "Source field for the user-product relation.",
    "Исходное поле связи пользователя и продукта."
  ),
  field(
    "form",
    "string",
    "סוג הנכס. אחד משמונת הערכים המתועדים מטה.",
    "Asset type. One of the eight documented form values below.",
    "Тип актива. Одно из восьми значений form ниже.",
    true
  ),
  field(
    "name",
    "string",
    "שם הנכס כפי שמופיע במאניטור.",
    "Asset name as shown in Moneytor.",
    "Название актива в Moneytor.",
    true
  ),
  field(
    "currency",
    "object",
    "מטבע הנכס עם value, name, sign ו-rate.",
    "Asset currency with value, name, sign, and rate.",
    "Валюта актива с value, name, sign и rate."
  ),
  field(
    "liquidity",
    "boolean|null",
    "האם הנכס נזיל. בחובות הערך עשוי להיות null.",
    "Whether the asset is liquid. Debts may return null.",
    "Ликвидность актива. Для долгов может быть null."
  ),
  field(
    "ownership",
    "number|null",
    "אחוז בעלות המשתמש בנכס.",
    "User ownership percentage.",
    "Процент владения пользователя."
  ),
  field(
    "insights",
    "string[]|null",
    "מזהי תובנות שמקושרות לנכס, אם קיימות.",
    "Insight ids linked to the asset, when available.",
    "ID инсайтов, связанных с активом, если есть."
  ),
  field(
    "updatedAt",
    "string",
    "זמן עדכון אחרון בפורמט ISO.",
    "Last update timestamp in ISO format.",
    "Время последнего обновления в формате ISO."
  ),
  field(
    "provider",
    "string|null",
    "שם ספק/מקור נתונים כאשר קיים.",
    "Provider or data source name when available.",
    "Поставщик или источник данных, если есть."
  ),
  field(
    "openfinance_asset_id",
    "string|null",
    "מזהה מקור Open Finance המקורי.",
    "Original Open Finance source asset id.",
    "Исходный ID актива Open Finance."
  ),
  field(
    "openfinance_connection_id",
    "string|null",
    "מזהה חיבור Open Finance המקורי.",
    "Original Open Finance connection id.",
    "Исходный ID подключения Open Finance."
  ),
  field(
    "openfinanceAssetId",
    "string|null",
    "מזהה Open Finance מנורמל ב-camelCase.",
    "Normalized Open Finance asset id in camelCase.",
    "Нормализованный ID актива Open Finance в camelCase."
  ),
  field(
    "openfinanceConnectionId",
    "string|null",
    "מזהה חיבור Open Finance מנורמל ב-camelCase.",
    "Normalized Open Finance connection id in camelCase.",
    "Нормализованный ID подключения Open Finance в camelCase."
  ),
  field(
    "quilttAccountId",
    "string|null",
    'מזהה חשבון Quiltt (בנק בארה"ב) ב-camelCase.',
    "Quiltt (US bank) account id in camelCase.",
    "ID счёта Quiltt (банк США) в camelCase."
  ),
  field(
    "quilttConnectionId",
    "string|null",
    "מזהה חיבור Quiltt ב-camelCase.",
    "Quiltt connection id in camelCase.",
    "ID подключения Quiltt в camelCase."
  ),
  field(
    "balanceInBaseCurrency",
    "number",
    "שווי הנכס במטבע הבסיס של התגובה.",
    "Asset balance in the response base currency.",
    "Стоимость актива в базовой валюте ответа.",
    true
  ),
  field(
    "partOfPortfolio",
    "number",
    "חלק הנכס מתוך התיק באחוזים.",
    "Asset share of the portfolio, as a percentage.",
    "Доля актива в портфеле в процентах.",
    true
  ),
];

export const apiAssetTypeDocs: ApiAssetTypeDoc[] = [
  {
    form: "bank",
    title: localized("בנק / פיקדון", "Bank / deposit", "Банк / депозит"),
    description: localized(
      'חשבונות עו"ש, פיקדונות ומזומן בנקאי.',
      "Checking accounts, deposits, and bank cash products.",
      "Текущие счета, депозиты и банковские денежные продукты."
    ),
    fields: [
      field(
        "country",
        "object",
        "מדינת החשבון.",
        "Account country.",
        "Страна счета."
      ),
      field(
        "accountType",
        "object",
        "סוג חשבון, למשל balance או saving.",
        "Account type, such as balance or saving.",
        "Тип счета, например balance или saving."
      ),
      field(
        "bank",
        "string",
        "שם הבנק או המוסד.",
        "Bank or institution name.",
        "Название банка или учреждения."
      ),
      field(
        "amount",
        "number",
        "יתרה נוכחית במטבע המקורי.",
        "Current balance in original currency.",
        "Текущий баланс в исходной валюте."
      ),
      field(
        "interest",
        "number",
        "ריבית שנתית, אם הוזנה.",
        "Annual interest, when entered.",
        "Годовая ставка, если указана."
      ),
      field(
        "monthlyDeposit",
        "number",
        "הפקדה חודשית לפיקדונות.",
        "Monthly deposit for savings products.",
        "Ежемесячное пополнение для депозитов."
      ),
      field(
        "closeExitPoint",
        "string",
        "נקודת יציאה קרובה לפיקדון.",
        "Nearest deposit exit point.",
        "Ближайшая дата выхода из депозита."
      ),
      field(
        "maturityDate",
        "string",
        "תאריך פירעון או סיום.",
        "Maturity or end date.",
        "Дата погашения или окончания."
      ),
      field(
        "bankNumber",
        "string|number",
        "מספר בנק.",
        "Bank number.",
        "Номер банка."
      ),
      field(
        "branchNumber",
        "string|number",
        "מספר סניף.",
        "Branch number.",
        "Номер отделения."
      ),
      field(
        "accountNumber",
        "string|number",
        "מספר חשבון.",
        "Account number.",
        "Номер счета."
      ),
      field(
        "tax",
        "number",
        "שיעור מס רלוונטי באחוזים.",
        "Relevant tax rate as a percentage.",
        "Соответствующая налоговая ставка в процентах."
      ),
    ],
  },
  {
    form: "pension",
    title: localized(
      "פנסיה / גמל / השתלמות",
      "Pension / provident / education fund",
      "Пенсия / накопительный фонд"
    ),
    description: localized(
      "חסכונות פנסיוניים וקופות שמגיעים ידנית או ממסלקה.",
      "Long-term savings and pension funds, entered manually or imported from Masleka.",
      "Долгосрочные пенсионные и накопительные продукты, вручную или из Masleka."
    ),
    fields: [
      field(
        "institution",
        "object",
        "גוף מנהל.",
        "Managing institution.",
        "Управляющая организация."
      ),
      field(
        "productType",
        "object",
        "סוג מוצר פנסיוני.",
        "Pension product type.",
        "Тип пенсионного продукта."
      ),
      field(
        "route",
        "object",
        "מסלול השקעה.",
        "Investment route.",
        "Инвестиционный трек."
      ),
      field(
        "number",
        "object",
        "מספר קופה/פוליסה.",
        "Fund or policy number.",
        "Номер фонда или полиса."
      ),
      field(
        "fund_id",
        "object",
        "מזהה קרן במקור הנתונים.",
        "Fund id from the source data.",
        "ID фонда в исходных данных."
      ),
      field(
        "fundId",
        "string",
        "מזהה קרן מנורמל.",
        "Normalized fund id.",
        "Нормализованный ID фонда."
      ),
      field(
        "amount",
        "number",
        "סכום צבור נוכחי.",
        "Current accumulated amount.",
        "Текущая накопленная сумма."
      ),
      field(
        "expectedYield",
        "number",
        "תשואה צפויה שנתית.",
        "Expected annual yield.",
        "Ожидаемая годовая доходность."
      ),
      field(
        "managementFeeFromSavings",
        "number",
        "דמי ניהול מצבירה.",
        "Management fee from savings.",
        "Комиссия от накоплений."
      ),
      field(
        "managementFeeFromDeposit",
        "number",
        "דמי ניהול מהפקדה.",
        "Management fee from deposits.",
        "Комиссия от взносов."
      ),
      field(
        "depositFrequency",
        "object",
        "תדירות הפקדות.",
        "Deposit frequency.",
        "Частота взносов."
      ),
      field(
        "monthlyDepositEmployee",
        "number",
        "הפקדת עובד חודשית.",
        "Monthly employee contribution.",
        "Ежемесячный взнос работника."
      ),
      field(
        "monthlyDepositEmployer",
        "number",
        "הפקדת מעסיק חודשית.",
        "Monthly employer contribution.",
        "Ежемесячный взнос работодателя."
      ),
      field(
        "yearsToRetirement",
        "number|null",
        "מספר שנים משוער לפרישה.",
        "Estimated years to retirement.",
        "Оценка лет до пенсии."
      ),
      field(
        "projectedMonthlyPension",
        "number",
        "קצבה חודשית צפויה ממסלקה.",
        "Projected monthly pension from Masleka.",
        "Прогноз месячной пенсии из Masleka."
      ),
      field(
        "investmentDistribution",
        "array",
        "חלוקת השקעה למסלולים, כאשר קיימת.",
        "Investment allocation by route, when available.",
        "Распределение инвестиций по трекам, если есть."
      ),
      field(
        "enrichedFund",
        "object",
        "מידע מועשר על הקרן ממאגרי תשואות.",
        "Enriched fund metadata from yield databases.",
        "Расширенные данные фонда из баз доходности."
      ),
      field(
        "additionalFundInfo",
        "object",
        "מידע משלים על מסלול ההשקעה.",
        "Additional investment route metadata.",
        "Дополнительные данные инвестиционного трека."
      ),
    ],
    nestedFieldGroups: [
      {
        name: "investmentDistribution[]",
        type: "array<object>",
        description: localized(
          "חלוקה למסלולי השקעה.",
          "Allocation across investment routes.",
          "Распределение по инвестиционным трекам."
        ),
        fields: [
          field(
            "routeName",
            "string",
            "שם המסלול.",
            "Route name.",
            "Название трека."
          ),
          field(
            "routeCode",
            "string",
            "קוד המסלול.",
            "Route code.",
            "Код трека."
          ),
          field(
            "amount",
            "number",
            "סכום במסלול.",
            "Amount in the route.",
            "Сумма в треке."
          ),
          field(
            "depositPercentage",
            "number",
            "אחוז הפקדה למסלול.",
            "Deposit percentage for the route.",
            "Процент взноса в трек."
          ),
        ],
      },
    ],
  },
  {
    form: "share",
    title: localized("ניירות ערך", "Stocks / securities", "Ценные бумаги"),
    description: localized(
      "תיק מניות, קרנות וניירות ערך עם פירוט החזקות.",
      "Brokerage portfolios with holding-level stock or fund rows.",
      "Брокерские портфели с детализацией бумаг и фондов."
    ),
    fields: [
      field(
        "broker",
        "string",
        "שם ברוקר או מוסד.",
        "Broker or institution name.",
        "Название брокера или учреждения."
      ),
      field(
        "stocksData",
        "array",
        "רשימת ניירות הערך בתיק.",
        "Holdings in the portfolio.",
        "Список бумаг в портфеле."
      ),
      field(
        "stocksOptions",
        "array",
        "אפשרויות חיפוש/בחירה ששימשו בממשק.",
        "Search/selection options used by the UI.",
        "Варианты поиска/выбора из интерфейса."
      ),
      field(
        "cash",
        "number",
        "מזומן בחשבון המסחר.",
        "Cash in the brokerage account.",
        "Денежный остаток на брокерском счете."
      ),
      field(
        "minimumFee",
        "number",
        "עמלת מינימום.",
        "Minimum fee.",
        "Минимальная комиссия."
      ),
      field(
        "shiftFee",
        "number",
        "עמלת העברה/פעולה.",
        "Transfer or transaction fee.",
        "Комиссия перевода или операции."
      ),
      field(
        "showAfterTax",
        "boolean",
        "האם להציג שווי אחרי מס.",
        "Whether after-tax value is enabled.",
        "Показывать ли стоимость после налога."
      ),
      field(
        "yesefTax",
        "boolean|null",
        "האם חל מס יסף.",
        "Whether surtax applies.",
        "Применяется ли дополнительный налог."
      ),
      field(
        "offsetLosses",
        "boolean",
        "האם לקזז הפסדים.",
        "Whether losses are offset.",
        "Учитывать ли зачет убытков."
      ),
      field(
        "ibkr_account_id",
        "string",
        "מזהה חשבון IBKR, אם קיים.",
        "IBKR account id, when available.",
        "ID счета IBKR, если есть."
      ),
    ],
    nestedFieldGroups: [
      {
        name: "stocksData[]",
        type: "array<object>",
        description: localized(
          "שורת החזקה בודדת.",
          "Single holding row.",
          "Одна строка позиции."
        ),
        fields: [
          field(
            "stockName",
            "string",
            "סימול או שם נייר.",
            "Ticker or security name.",
            "Тикер или название бумаги."
          ),
          field(
            "amount",
            "number",
            "כמות יחידות.",
            "Number of units.",
            "Количество единиц."
          ),
          field(
            "purchasePrice",
            "number",
            "מחיר קנייה ליחידה.",
            "Purchase price per unit.",
            "Цена покупки за единицу."
          ),
          field(
            "purchaseDate",
            "string|null",
            "תאריך קנייה.",
            "Purchase date.",
            "Дата покупки."
          ),
          field(
            "stockPrice",
            "number",
            "מחיר נוכחי במטבע הנייר.",
            "Current price in security currency.",
            "Текущая цена в валюте бумаги."
          ),
          field(
            "stockPriceInBaseCurrency",
            "number",
            "מחיר נוכחי במטבע הבסיס.",
            "Current price in base currency.",
            "Текущая цена в базовой валюте."
          ),
          field(
            "currency",
            "object",
            "מטבע נייר הערך.",
            "Security currency.",
            "Валюта бумаги."
          ),
          field(
            "totalWorth",
            "number",
            "שווי כולל במטבע המקורי.",
            "Total value in original currency.",
            "Общая стоимость в исходной валюте."
          ),
          field(
            "totalWorthInBaseCurrency",
            "number",
            "שווי כולל במטבע הבסיס.",
            "Total value in base currency.",
            "Общая стоимость в базовой валюте."
          ),
          field(
            "balanceInBaseCurrency",
            "number",
            "שווי החזקה אחרי התאמות במטבע הבסיס.",
            "Adjusted holding value in base currency.",
            "Скорректированная стоимость позиции в базовой валюте."
          ),
        ],
      },
    ],
  },
  {
    form: "rsu",
    title: localized(
      "RSU / אופציות / ESPP",
      "RSU / options / ESPP",
      "RSU / опционы / ESPP"
    ),
    description: localized(
      "מענקי מניות, אופציות ו-ESPP עם חישובי הבשלה ומס.",
      "Equity compensation grants with vesting and tax calculations.",
      "Долевые компенсации с расчетами вестинга и налогов."
    ),
    fields: [
      field(
        "broker",
        "string",
        "סוג או שם ברוקר/תוכנית.",
        "Broker or plan type/name.",
        "Тип или название брокера/плана."
      ),
      field(
        "rsuData",
        "array",
        "רשימת מענקים או אופציות.",
        "Grant or option rows.",
        "Список грантов или опционов."
      ),
      field(
        "rsuOptions",
        "array",
        "אפשרויות חיפוש/בחירה ששימשו בממשק.",
        "Search/selection options used by the UI.",
        "Варианты поиска/выбора из интерфейса."
      ),
      field(
        "showAfterTax",
        "boolean",
        "האם לחשב אחרי מס.",
        "Whether after-tax calculation is enabled.",
        "Включен ли расчет после налога."
      ),
      field(
        "showVestedOnly",
        "boolean",
        "האם לכלול רק יחידות שהבשילו.",
        "Whether only vested units are included.",
        "Учитывать только vested единицы."
      ),
      field(
        "taxRate",
        "number",
        "שיעור מס באחוזים.",
        "Tax rate percentage.",
        "Налоговая ставка в процентах."
      ),
      field(
        "yesefTax",
        "boolean|null",
        "האם חל מס יסף.",
        "Whether surtax applies.",
        "Применяется ли дополнительный налог."
      ),
      field(
        "optionGrantType",
        "string|null",
        "סוג מענק אופציות.",
        "Option grant type.",
        "Тип опционного гранта."
      ),
      field(
        "ipoDate",
        "string|null",
        "תאריך IPO רלוונטי לאופציות.",
        "IPO date relevant for options.",
        "Дата IPO для опционов."
      ),
      field(
        "ipoPrice",
        "number|null",
        "מחיר IPO רלוונטי לאופציות.",
        "IPO price relevant for options.",
        "Цена IPO для опционов."
      ),
      field(
        "showSaleInTwoYears",
        "boolean",
        "האם לחשב תרחיש מכירה אחרי שנתיים.",
        "Whether two-year sale scenario is enabled.",
        "Включен ли сценарий продажи через два года."
      ),
      field(
        "stockName",
        "string",
        "שם מניה ראשי ברמת המוצר.",
        "Primary stock name at product level.",
        "Основное название акции на уровне продукта."
      ),
      field(
        "stockPrice",
        "string|number",
        "מחיר מניה ראשי.",
        "Primary stock price.",
        "Основная цена акции."
      ),
      field(
        "stockPriceInBaseCurrency",
        "number",
        "מחיר מניה במטבע הבסיס.",
        "Stock price in base currency.",
        "Цена акции в базовой валюте."
      ),
      field(
        "stockMarket",
        "string|null",
        "שוק/בורסה.",
        "Market or exchange.",
        "Рынок или биржа."
      ),
    ],
    nestedFieldGroups: [
      {
        name: "rsuData[]",
        type: "array<object>",
        description: localized(
          "שורת מענק בודדת.",
          "Single grant row.",
          "Одна строка гранта."
        ),
        fields: [
          field(
            "stockName",
            "string",
            "שם או סימול מניה.",
            "Stock name or ticker.",
            "Название или тикер акции."
          ),
          field(
            "totalAmount",
            "number",
            "סך יחידות במענק.",
            "Total units in the grant.",
            "Всего единиц в гранте."
          ),
          field(
            "vestedAmount",
            "number",
            "יחידות שהבשילו.",
            "Vested units.",
            "Vested единицы."
          ),
          field(
            "effectiveAmount",
            "number",
            "כמות אפקטיבית לחישוב.",
            "Effective units used for valuation.",
            "Эффективное количество для оценки."
          ),
          field(
            "grantPrice",
            "number",
            "מחיר מענק/מימוש.",
            "Grant or strike price.",
            "Цена гранта или исполнения."
          ),
          field(
            "grantDate",
            "string|null",
            "תאריך מענק.",
            "Grant date.",
            "Дата гранта."
          ),
          field(
            "buyPrice",
            "number|null",
            "מחיר קנייה ב-ESPP.",
            "ESPP buy price.",
            "Цена покупки ESPP."
          ),
          field(
            "stockPrice",
            "number",
            "מחיר מניה נוכחי.",
            "Current stock price.",
            "Текущая цена акции."
          ),
          field(
            "currency",
            "object",
            "מטבע המניה.",
            "Stock currency.",
            "Валюта акции."
          ),
          field(
            "totalWorth",
            "number",
            "שווי כולל במטבע המקורי.",
            "Total value in original currency.",
            "Общая стоимость в исходной валюте."
          ),
          field(
            "totalWorthInBaseCurrency",
            "number",
            "שווי כולל במטבע הבסיס.",
            "Total value in base currency.",
            "Общая стоимость в базовой валюте."
          ),
          field(
            "balanceInBaseCurrency",
            "number",
            "שווי אחרי בחירת vested/מס במטבע הבסיס.",
            "Value after vesting/tax settings in base currency.",
            "Стоимость после настроек vesting/налога в базовой валюте."
          ),
        ],
      },
    ],
  },
  {
    form: "crypto",
    title: localized("קריפטו", "Crypto", "Крипто"),
    description: localized(
      "ארנקי קריפטו והחזקות מטבעות.",
      "Crypto wallets and coin holdings.",
      "Криптокошельки и позиции по монетам."
    ),
    fields: [
      field(
        "cryptoData",
        "array",
        "רשימת מטבעות בארנק.",
        "Coin holdings in the wallet.",
        "Монеты в кошельке."
      ),
      field(
        "cryptoOptions",
        "array",
        "אפשרויות חיפוש/בחירה ששימשו בממשק.",
        "Search/selection options used by the UI.",
        "Варианты поиска/выбора из интерфейса."
      ),
      field(
        "cash",
        "number",
        "מזומן משויך לארנק, אם קיים.",
        "Cash associated with the wallet, when available.",
        "Денежный остаток кошелька, если есть."
      ),
      field(
        "showAfterTax",
        "boolean",
        "האם לחשב אחרי מס.",
        "Whether after-tax calculation is enabled.",
        "Включен ли расчет после налога."
      ),
      field(
        "yesefTax",
        "boolean",
        "האם חל מס יסף.",
        "Whether surtax applies.",
        "Применяется ли дополнительный налог."
      ),
      field(
        "offsetLosses",
        "boolean",
        "האם לקזז הפסדי קריפטו.",
        "Whether crypto losses are offset.",
        "Учитывать ли зачет крипто-убытков."
      ),
    ],
    nestedFieldGroups: [
      {
        name: "cryptoData[]",
        type: "array<object>",
        description: localized(
          "החזקה במטבע בודד.",
          "Single coin holding.",
          "Одна позиция по монете."
        ),
        fields: [
          field(
            "coinName",
            "string",
            "סימול המטבע.",
            "Coin symbol.",
            "Символ монеты."
          ),
          field(
            "amount",
            "number",
            "כמות מטבעות.",
            "Coin amount.",
            "Количество монет."
          ),
          field(
            "purchasePrice",
            "number",
            "מחיר קנייה.",
            "Purchase price.",
            "Цена покупки."
          ),
          field(
            "purchaseDate",
            "string|null",
            "תאריך קנייה.",
            "Purchase date.",
            "Дата покупки."
          ),
          field(
            "purchaseFee",
            "number|null",
            "עמלת קנייה.",
            "Purchase fee.",
            "Комиссия покупки."
          ),
          field(
            "coinRate",
            "number",
            "שער מטבע נוכחי.",
            "Current coin rate.",
            "Текущий курс монеты."
          ),
          field(
            "totalWorth",
            "number",
            "שווי כולל במטבע המקורי.",
            "Total value in original currency.",
            "Общая стоимость в исходной валюте."
          ),
          field(
            "totalWorthInBaseCurrency",
            "number",
            "שווי כולל במטבע הבסיס.",
            "Total value in base currency.",
            "Общая стоимость в базовой валюте."
          ),
          field(
            "balanceInBaseCurrency",
            "number",
            "שווי החזקה במטבע הבסיס.",
            "Holding value in base currency.",
            "Стоимость позиции в базовой валюте."
          ),
        ],
      },
    ],
  },
  {
    form: "debt",
    title: localized(
      "חוב / הלוואה / משכנתא",
      "Debt / loan / mortgage",
      "Долг / кредит / ипотека"
    ),
    description: localized(
      "הלוואות, משכנתאות וחיובי אשראי שמוצגים כחובות.",
      "Loans, mortgages, and credit liabilities returned as debt assets.",
      "Кредиты, ипотеки и кредитные обязательства как долговые активы."
    ),
    fields: [
      field(
        "debtInstitution",
        "string",
        "שם הגוף המלווה.",
        "Lending institution.",
        "Кредитная организация."
      ),
      field(
        "debtType",
        "string",
        "סוג חוב, למשל loan או mortgage.",
        "Debt type, such as loan or mortgage.",
        "Тип долга, например loan или mortgage."
      ),
      field(
        "startDate",
        "string",
        "תאריך התחלת החוב.",
        "Debt start date.",
        "Дата начала долга."
      ),
      field(
        "routesData",
        "array",
        "מסלולי החוב או ההלוואה.",
        "Debt or loan tracks.",
        "Треки долга или кредита."
      ),
      field(
        "returnType",
        "string",
        "סוג החזר, אם קיים.",
        "Repayment type, when available.",
        "Тип погашения, если есть."
      ),
      field(
        "graceType",
        "object",
        "סוג גרייס.",
        "Grace type.",
        "Тип льготного периода."
      ),
      field(
        "graceYears",
        "object",
        "משך גרייס בשנים.",
        "Grace duration in years.",
        "Длительность льготного периода в годах."
      ),
    ],
    nestedFieldGroups: [
      {
        name: "routesData[]",
        type: "array<object>",
        description: localized(
          "מסלול חוב בודד.",
          "Single debt track.",
          "Один долговой трек."
        ),
        fields: [
          field(
            "remainder",
            "number",
            "יתרת חוב במסלול.",
            "Remaining balance for the track.",
            "Остаток долга по треку."
          ),
          field(
            "trackInterestType",
            "object",
            "סוג ריבית המסלול.",
            "Track interest type.",
            "Тип ставки трека."
          ),
          field(
            "interest",
            "number",
            "ריבית שנתית.",
            "Annual interest rate.",
            "Годовая процентная ставка."
          ),
          field(
            "monthlyRepayment",
            "number",
            "החזר חודשי.",
            "Monthly repayment.",
            "Ежемесячный платеж."
          ),
          field(
            "originalSum",
            "number",
            "סכום מקורי.",
            "Original principal.",
            "Первоначальная сумма."
          ),
          field(
            "debtPeriodInMonths",
            "number",
            "משך החוב בחודשים.",
            "Debt duration in months.",
            "Срок долга в месяцах."
          ),
        ],
      },
    ],
  },
  {
    form: "realestate",
    title: localized('נדל"ן', "Real estate", "Недвижимость"),
    description: localized(
      'נכסי נדל"ן עם פרטי נכס, שכירות ותזרים.',
      "Properties with valuation, address, rent, and cashflow details.",
      "Объекты недвижимости с оценкой, адресом, арендой и cashflow."
    ),
    fields: [
      field(
        "value",
        "number",
        "שווי נוכחי.",
        "Current value.",
        "Текущая стоимость."
      ),
      field(
        "purchasePrice",
        "number",
        "מחיר קנייה.",
        "Purchase price.",
        "Цена покупки."
      ),
      field(
        "purchaseDate",
        "string",
        "תאריך רכישה.",
        "Purchase date.",
        "Дата покупки."
      ),
      field(
        "purchaseExpenses",
        "number",
        "הוצאות רכישה.",
        "Purchase expenses.",
        "Расходы покупки."
      ),
      field(
        "linkedMortgage",
        "object",
        "משכנתא מקושרת.",
        "Linked mortgage.",
        "Связанная ипотека."
      ),
      field(
        "expectedValueIncrease",
        "number",
        "עליית ערך צפויה.",
        "Expected value increase.",
        "Ожидаемый рост стоимости."
      ),
      field(
        "saleCommission",
        "number",
        "עמלת מכירה.",
        "Sale commission.",
        "Комиссия продажи."
      ),
      field(
        "profitTax",
        "number",
        "מס שבח/רווח.",
        "Profit tax.",
        "Налог на прибыль."
      ),
      field(
        "address",
        "string",
        "כתובת מלאה.",
        "Full address.",
        "Полный адрес."
      ),
      field("country", "object", "מדינה.", "Country.", "Страна."),
      field("city", "string", "עיר.", "City.", "Город."),
      field("street", "string", "רחוב.", "Street.", "Улица."),
      field(
        "builtArea",
        "number",
        "שטח בנוי.",
        "Built area.",
        "Площадь постройки."
      ),
      field(
        "propertyType",
        "object",
        "סוג נכס.",
        "Property type.",
        "Тип объекта."
      ),
      field(
        "bedrooms",
        "number",
        "מספר חדרי שינה.",
        "Bedroom count.",
        "Количество спален."
      ),
      field(
        "bathrooms",
        "number",
        "מספר חדרי רחצה.",
        "Bathroom count.",
        "Количество ванных."
      ),
      field(
        "parking",
        "number",
        "מספר חניות.",
        "Parking spaces.",
        "Парковочные места."
      ),
      field(
        "rent",
        "number",
        "שכירות חודשית/תקופתית.",
        "Rent amount.",
        "Сумма аренды."
      ),
      field("rentType", "object", "סוג שכירות.", "Rent type.", "Тип аренды."),
      field(
        "cashflowsData",
        "array",
        "הכנסות והוצאות לנכס.",
        "Property income and expense rows.",
        "Доходы и расходы объекта."
      ),
      field("latitude", "number", "קו רוחב.", "Latitude.", "Широта."),
      field("longitude", "number", "קו אורך.", "Longitude.", "Долгота."),
    ],
    nestedFieldGroups: [
      {
        name: "cashflowsData[]",
        type: "array<object>",
        description: localized(
          'שורת תזרים נדל"ן.',
          "Single real-estate cashflow row.",
          "Одна строка cashflow недвижимости."
        ),
        fields: [
          field(
            "expenditureType",
            "object",
            "סוג הכנסה/הוצאה.",
            "Income or expense type.",
            "Тип дохода или расхода."
          ),
          field("frequency", "object", "תדירות.", "Frequency.", "Частота."),
          field(
            "fixedAmountPercentage",
            "boolean",
            "האם הסכום הוא אחוז.",
            "Whether amount is a percentage.",
            "Является ли сумма процентом."
          ),
          field(
            "amount",
            "number",
            "סכום השורה.",
            "Row amount.",
            "Сумма строки."
          ),
          field(
            "sum",
            "number",
            "סכום מחושב.",
            "Calculated sum.",
            "Расчетная сумма."
          ),
        ],
      },
    ],
  },
  {
    form: "other",
    title: localized("אחר", "Other", "Другое"),
    description: localized(
      "נכסים כלליים שאינם משתייכים לסוג ייעודי.",
      "Generic assets that do not fit a dedicated form.",
      "Общие активы, не подходящие под отдельный тип."
    ),
    fields: [
      field(
        "currentValue",
        "number",
        "שווי נוכחי.",
        "Current value.",
        "Текущая стоимость."
      ),
      field(
        "expectedMonthlyIncome",
        "number",
        "הכנסה חודשית צפויה.",
        "Expected monthly income.",
        "Ожидаемый месячный доход."
      ),
      field(
        "expectedMonthlyExpenses",
        "number",
        "הוצאות חודשיות צפויות.",
        "Expected monthly expenses.",
        "Ожидаемые месячные расходы."
      ),
      field(
        "expectedSellingExpenses",
        "number",
        "הוצאות מכירה צפויות.",
        "Expected selling expenses.",
        "Ожидаемые расходы продажи."
      ),
      field(
        "buyingPrice",
        "number",
        "מחיר קנייה.",
        "Buying price.",
        "Цена покупки."
      ),
      field("country", "object", "מדינה.", "Country.", "Страна."),
      field(
        "managementFee",
        "number",
        "דמי ניהול.",
        "Management fee.",
        "Комиссия управления."
      ),
      field(
        "purchaseDate",
        "string",
        "תאריך רכישה.",
        "Purchase date.",
        "Дата покупки."
      ),
      field(
        "predictedValueIncrease",
        "number",
        "עליית ערך צפויה.",
        "Predicted value increase.",
        "Прогноз роста стоимости."
      ),
    ],
  },
];

export const apiEndpointDocs: ApiEndpointDoc[] = [
  {
    kind: "assetWorth",
    method: "GET",
    path: "/api/v1/assetWorth",
    summary: {
      HEB: "שווי נכסים ונטו לפי הדשבורד",
      ENG: "Dashboard-style asset worth and net worth",
      RUS: "Стоимость активов и net worth как в dashboard",
    },
    description: {
      HEB: "מחזיר תמונת מצב מסוכמת: סך נכסים, חובות, שווי נטו ורשימת מוצרים קצרה.",
      ENG: "Returns a summarized financial snapshot: assets, debts, net worth, and a compact product summary.",
      RUS: "Возвращает сводный финансовый снимок: активы, долги, net worth и краткую сводку продуктов.",
    },
    useWhen: {
      HEB: "כשצריך מספרים למסך בית, אנליטיקה או Agent שרוצה להבין את התמונה הכוללת.",
      ENG: "Use it for dashboards, analytics, or agents that need the overall financial picture.",
      RUS: "Используйте для dashboard, аналитики или агентов, которым нужна общая картина.",
    },
    queryParams: [],
    responseFields: [
      {
        name: "ok",
        type: "boolean",
        required: true,
        description: commonDescriptions.ok,
      },
      {
        name: "asOf",
        type: "string",
        required: true,
        description: commonDescriptions.asOf,
      },
      {
        name: "baseCurrency",
        type: "string",
        required: true,
        description: commonDescriptions.baseCurrency,
      },
      {
        name: "totalBalances",
        type: "object",
        required: true,
        description: {
          HEB: "אובייקט סיכומי יתרות לפי קטגוריות נכסים וחובות.",
          ENG: "Aggregate balances grouped by asset and debt categories.",
          RUS: "Агрегированные балансы по категориям активов и долгов.",
        },
      },
      {
        name: "netWorth",
        type: "number",
        required: true,
        description: {
          HEB: "נכסים פחות חובות במטבע הבסיס.",
          ENG: "Assets minus debts in the base currency.",
          RUS: "Активы минус долги в базовой валюте.",
        },
      },
      {
        name: "productsSummary",
        type: "array",
        required: true,
        description: {
          HEB: "רשימת מוצרים קצרה עם id, form, name ו-balanceInBase.",
          ENG: "Compact product list with id, form, name, and balanceInBase.",
          RUS: "Краткий список продуктов с id, form, name и balanceInBase.",
        },
      },
    ],
    exampleResponse: {
      ok: true,
      asOf: "2026-05-23T08:18:26.341Z",
      baseCurrency: "ILS",
      totalBalances: {
        assets: 6707996.69,
        debts: 188464.84,
        bankAssets: 1286905,
        pensionAssets: 3213333,
        shareAssets: 1069765.84,
        cryptoAssets: 1120005.45,
        lastUpdate: {
          bankAssets: "2026-05-20T19:00:58.479538+00:00",
          pensionAssets: "2025-05-08T15:26:55.130484+00:00",
        },
      },
      netWorth: 6519531.85,
      productsSummary: [
        {
          id: "6255",
          form: "bank",
          name: "אופן פיננס סנדבוקס פקדון",
          balanceInBase: 16819,
        },
        {
          id: "6028",
          form: "crypto",
          name: "שם ארנק הקריפטו",
          balanceInBase: 1120005.45,
        },
      ],
    },
  },
  {
    kind: "assets",
    method: "GET",
    path: "/api/v1/assets",
    summary: {
      HEB: "רשימת נכסים מלאה ומועשרת",
      ENG: "Full enriched assets list",
      RUS: "Полный расширенный список активов",
    },
    description: {
      HEB: "מחזיר כל מוצר פיננסי עם שדות מקוריים, חישובי שווי במטבע בסיס ושדות אחידים לאינטגרציות.",
      ENG: "Returns every financial product with source fields, base-currency calculations, and normalized integration fields.",
      RUS: "Возвращает каждый финансовый продукт с исходными полями, расчетами в базовой валюте и нормализованными полями.",
    },
    useWhen: {
      HEB: "כשצריך לבנות סנכרון, Agent שמנתח מוצר-מוצר, או אינטגרציה שצריכה פירוט holdings.",
      ENG: "Use it for sync jobs, per-asset analysis, or integrations that need holding-level detail.",
      RUS: "Используйте для синхронизации, анализа по активам или интеграций с деталями holdings.",
    },
    queryParams: [],
    responseFields: [
      {
        name: "ok",
        type: "boolean",
        required: true,
        description: commonDescriptions.ok,
      },
      {
        name: "asOf",
        type: "string",
        required: true,
        description: commonDescriptions.asOf,
      },
      {
        name: "baseCurrency",
        type: "string",
        required: true,
        description: commonDescriptions.baseCurrency,
      },
      {
        name: "count",
        type: "number",
        required: true,
        description: {
          HEB: "מספר הנכסים במערך assets.",
          ENG: "Number of assets in the assets array.",
          RUS: "Количество активов в массиве assets.",
        },
      },
      {
        name: "assets",
        type: "array",
        required: true,
        description: {
          HEB: "מערך נכסים. לכל נכס יש id, form, name, currency, balanceInBaseCurrency ו-partOfPortfolio.",
          ENG: "Asset array. Each asset includes id, form, name, currency, balanceInBaseCurrency, and partOfPortfolio.",
          RUS: "Массив активов. Каждый содержит id, form, name, currency, balanceInBaseCurrency и partOfPortfolio.",
        },
      },
    ],
    exampleResponse: {
      ok: true,
      asOf: "2026-05-23T08:39:05.559Z",
      baseCurrency: "ILS",
      count: 2,
      assets: [
        {
          id: "6255",
          productId: 6255,
          form: "bank",
          name: "אופן פיננס סנדבוקס פקדון",
          liquidity: true,
          ownership: 100,
          currency: { value: "ILS", name: "", sign: "", rate: -1 },
          balanceInBaseCurrency: 16819,
          partOfPortfolio: 0.2507,
          updatedAt: "2026-05-19T17:00:55.816049+00:00",
        },
        {
          id: "6028",
          productId: 6028,
          form: "crypto",
          name: "שם ארנק הקריפטו",
          cryptoData: [
            {
              coinName: "BTC",
              amount: 5,
              coinRate: 74719.35,
              totalWorthInBaseCurrency: 1120005.45,
              balanceInBaseCurrency: 1120005.45,
            },
          ],
          balanceInBaseCurrency: 1120005.45,
          partOfPortfolio: 16.6966,
        },
      ],
    },
  },
  {
    kind: "transactions",
    method: "GET",
    path: "/api/v1/transactions",
    summary: {
      HEB: "תנועות תזרים",
      ENG: "Cashflow transactions",
      RUS: "Транзакции cashflow",
    },
    description: {
      HEB: "מחזיר תנועות Open Finance ותנועות ידניות, עם סינון תאריכים ו-limit.",
      ENG: "Returns Open Finance and manual cashflow transactions with date filtering and a limit.",
      RUS: "Возвращает Open Finance и ручные транзакции с фильтром дат и limit.",
    },
    useWhen: {
      HEB: "כשצריך לנתח הוצאות, הכנסות, קטגוריות או לבנות Agent תזרימי.",
      ENG: "Use it to analyze expenses, income, categories, or build cashflow agents.",
      RUS: "Используйте для анализа расходов, доходов, категорий или cashflow-агентов.",
    },
    queryParams: [
      {
        name: "from",
        type: "ISO date string",
        defaultValue: "none",
        description: {
          HEB: "תאריך התחלה כולל. ערך לא תקין ייחשב כאילו לא נשלח.",
          ENG: "Inclusive start date. Invalid values are treated as missing.",
          RUS: "Начальная дата включительно. Некорректные значения игнорируются.",
        },
      },
      {
        name: "to",
        type: "ISO date string",
        defaultValue: "none",
        description: {
          HEB: "תאריך סיום כולל. ערך לא תקין ייחשב כאילו לא נשלח.",
          ENG: "Inclusive end date. Invalid values are treated as missing.",
          RUS: "Конечная дата включительно. Некорректные значения игнорируются.",
        },
      },
      {
        name: "limit",
        type: "number",
        defaultValue: "500",
        description: {
          HEB: "מספר תנועות להחזיר בעמוד אחד. מוגבל לטווח 1-2000.",
          ENG: "Number of transactions to return per page. Clamped to 1-2000.",
          RUS: "Количество транзакций на странице. Ограничивается диапазоном 1-2000.",
        },
      },
      {
        name: "offset",
        type: "number",
        defaultValue: "0",
        description: {
          HEB: "מספר התנועות לדלג עליהן מתחילת התוצאות (למיון לפי תאריך, מהחדש לישן). השתמשו ב-nextOffset מהתשובה הקודמת כדי לדפדף ולהביא את כל התנועות.",
          ENG: "Number of transactions to skip from the start of the (newest-first) sorted results. Use the previous response's nextOffset to page through and retrieve every transaction.",
          RUS: "Количество транзакций для пропуска от начала отсортированных (сначала новые) результатов. Используйте nextOffset из предыдущего ответа для постраничного получения всех транзакций.",
        },
      },
    ],
    responseFields: [
      {
        name: "ok",
        type: "boolean",
        required: true,
        description: commonDescriptions.ok,
      },
      {
        name: "asOf",
        type: "string",
        required: true,
        description: commonDescriptions.asOf,
      },
      {
        name: "count",
        type: "number",
        required: true,
        description: {
          HEB: "מספר התנועות שחזרו אחרי limit.",
          ENG: "Number of returned transactions after applying limit.",
          RUS: "Количество возвращенных транзакций после limit.",
        },
      },
      {
        name: "totalAvailable",
        type: "number",
        required: true,
        description: {
          HEB: "מספר התנועות הזמינות אחרי סינון תאריכים (בכל העמודים).",
          ENG: "Total number of transactions available after date filtering (across all pages).",
          RUS: "Общее количество доступных транзакций после фильтра по датам (по всем страницам).",
        },
      },
      {
        name: "offset",
        type: "number",
        required: true,
        description: {
          HEB: "ה-offset שהוחל על הבקשה הנוכחית.",
          ENG: "The offset applied to the current request.",
          RUS: "Смещение (offset), применённое к текущему запросу.",
        },
      },
      {
        name: "hasMore",
        type: "boolean",
        required: true,
        description: {
          HEB: "האם קיימות תנועות נוספות מעבר לעמוד הנוכחי.",
          ENG: "Whether more transactions exist beyond the current page.",
          RUS: "Есть ли ещё транзакции за пределами текущей страницы.",
        },
      },
      {
        name: "nextOffset",
        type: "number | null",
        required: true,
        description: {
          HEB: "ה-offset שיש להשתמש בו בבקשה הבאה כדי להמשיך לדפדף. null אם אין עוד תנועות.",
          ENG: "The offset to use on the next request to keep paging. null when there are no more transactions.",
          RUS: "Смещение для использования в следующем запросе для продолжения пагинации. null, если больше транзакций нет.",
        },
      },
      {
        name: "transactions",
        type: "array",
        required: true,
        description: {
          HEB: "מערך תנועות עם id, date, bookingDate, transactionDate, valueDate, amount, currency, description, extra_info, category, accountId, accountNumber ו-type.",
          ENG: "Transaction array with id, date, bookingDate, transactionDate, valueDate, amount, currency, description, extra_info, category, accountId, accountNumber, and type.",
          RUS: "Массив транзакций с id, date, bookingDate, transactionDate, valueDate, amount, currency, description, extra_info, category, accountId, accountNumber и type.",
        },
      },
    ],
    exampleResponse: {
      ok: true,
      asOf: "2026-05-23T08:39:37.419Z",
      count: 2,
      totalAvailable: 117,
      offset: 0,
      limit: 2,
      hasMore: true,
      nextOffset: 2,
      transactions: [
        {
          id: "01KQRZXN78ZD6DZ8NM8YC1YSCY",
          date: "2026-02-09",
          bookingDate: "2026-02-09",
          transactionDate: "2026-02-08",
          valueDate: "2026-02-09",
          amount: 1800,
          currency: "ILS",
          description: "מב. הפועלים-י",
          extra_info: "העברה מחשבון 12345",
          category: "BANK_TRANSFER",
          accountId: "QUNDT1VOVCNU...",
          accountNumber: "123456xxxxxx7890",
          type: "CHECKING",
        },
        {
          id: "01KQRZXN78RHYEC5G5DZ99E7GT",
          date: "2026-03-10",
          bookingDate: "2026-03-10",
          transactionDate: "2026-03-08",
          valueDate: "2026-03-10",
          amount: -3236.36,
          currency: "ILS",
          description: "לאומי מאסטרקרד",
          extra_info: null,
          category: "CREDIT_CARD_CHECKING",
          accountId: "QUNDT1VOVCNU...",
          accountNumber: "547707xxxxxx3027",
          type: "CHECKING",
        },
      ],
    },
  },
  {
    kind: "insurance",
    method: "GET",
    path: "/api/v1/insurance",
    summary: {
      HEB: "פוליסות ביטוח מהר הביטוח",
      ENG: "Har Bituach insurance policies",
      RUS: "Полисы страхования Har Bituach",
    },
    description: {
      HEB: "מחזיר את דשבורד הביטוח: סיכום, מבוטחים ופוליסות פעילות עם כיסויים ופרמיה. כולל בני משפחה אם יש ייבוא משותף. לקריאה בלבד — לא מריץ ייבוא חדש.",
      ENG: "Returns the insurance dashboard: summary, insured members, and active policies with coverages and premiums. Includes family imports when present. Read-only — does not start a new Har Bituach import.",
      RUS: "Возвращает дашборд страхования: сводку, застрахованных и активные полисы с покрытиями и премиями. Включает семейный импорт, если он есть. Только чтение — новый импорт Har Bituach не запускается.",
    },
    useWhen: {
      HEB: "כשצריך לסנכרן פוליסות, פרמיות חודשיות/שנתיות או כיסויים ל-Agent / אינטגרציה.",
      ENG: "Use it to sync policies, monthly/annual premiums, or coverages into an agent or integration.",
      RUS: "Используйте для синхронизации полисов, премий и покрытий в агент или интеграцию.",
    },
    queryParams: [],
    responseFields: [
      {
        name: "ok",
        type: "boolean",
        required: true,
        description: commonDescriptions.ok,
      },
      {
        name: "asOf",
        type: "string",
        required: true,
        description: commonDescriptions.asOf,
      },
      {
        name: "baseCurrency",
        type: "string",
        required: true,
        description: commonDescriptions.baseCurrency,
      },
      {
        name: "state",
        type: "string",
        required: true,
        description: {
          HEB: "מצב הייבוא: empty, pending, dashboard או failed.",
          ENG: "Import state: empty, pending, dashboard, or failed.",
          RUS: "Состояние импорта: empty, pending, dashboard или failed.",
        },
      },
      {
        name: "pendingCount",
        type: "number",
        required: true,
        description: {
          HEB: "כמה ייבואים עדיין ממתינים לחתימה/נתונים.",
          ENG: "How many imports are still waiting for signature or data.",
          RUS: "Сколько импортов ещё ждут подписи или данных.",
        },
      },
      {
        name: "summary",
        type: "object",
        required: true,
        description: {
          HEB: "סיכום: מספר פוליסות פעילות, חברות, עלות חודשית ושנתית, תאריך עדכון ושם מבוטח.",
          ENG: "Summary: active policy count, companies, monthly/annual cost, last updated, and insured name.",
          RUS: "Сводка: число активных полисов, компании, месячная/годовая стоимость, дата обновления и имя застрахованного.",
        },
      },
      {
        name: "members",
        type: "array",
        required: true,
        description: {
          HEB: "מבוטחים במשפחה (personId + name).",
          ENG: "Insured family members (personId + name).",
          RUS: "Застрахованные члены семьи (personId + name).",
        },
      },
      {
        name: "policies",
        type: "array",
        required: true,
        description: {
          HEB: "פוליסות עם מספר, חברה, קטגוריה, פרמיה, תקופת חידוש וכיסויים.",
          ENG: "Policies with number, company, category, premiums, renewal period, and coverages.",
          RUS: "Полисы с номером, компанией, категорией, премиями, периодом продления и покрытиями.",
        },
      },
      {
        name: "failedSubjects",
        type: "array",
        required: true,
        description: {
          HEB: "ייבואים שנכשלו, עם סיבת כשל (invalid_data / not_signed / generic).",
          ENG: "Failed imports, with reason (invalid_data / not_signed / generic).",
          RUS: "Неудачные импорты с причиной (invalid_data / not_signed / generic).",
        },
      },
    ],
    exampleResponse: {
      ok: true,
      asOf: "2026-09-29T12:00:00.000Z",
      baseCurrency: "ILS",
      state: "dashboard",
      pendingCount: 0,
      summary: {
        activePolicies: 1,
        insuranceCompanies: 1,
        monthlyCost: 100,
        annualCost: 1200,
        lastUpdated: null,
        insuredName: "ישראל ישראלי",
      },
      members: [{ personId: "012345678", name: "ישראל ישראלי" }],
      policies: [
        {
          id: "012345678-health-111",
          policyNumber: 111,
          title: "בריאות",
          company: "הראל",
          companyShort: "הראל",
          categoryKey: "health",
          categoryLabelHeb: "בריאות",
          categoryLabelEng: "Health",
          status: "active",
          monthlyPremium: 100,
          annualPremium: 1200,
          renewalPeriod: "01/2026 - 12/2026",
          personId: "012345678",
          insuredName: "ישראל ישראלי",
          isFamilyAccount: false,
          coverages: [
            {
              mainBranch: "בריאות",
              subBranch: "ניתוחים",
              displayLabel: "ניתוחים",
              monthlyPremium: 100,
              planClassification: "",
              premiumType: "חודשית",
              productType: "בריאות",
            },
          ],
        },
      ],
      failedSubjects: [],
    },
  },
];

export const apiErrorExamples = [
  {
    status: 401,
    title: "Invalid API key",
    body: { ok: false, error: "Invalid API key" },
  },
  {
    status: 401,
    title: "API token expired",
    body: {
      ok: false,
      error: "API token expired",
      code: "api_token_expired",
      message:
        "This API token has expired. Create a new token in Moneytor API settings.",
      renew_url: "https://app.moneytor.co.il/settings#api",
    },
  },
  {
    status: 403,
    title: "Premium subscription required",
    body: { ok: false, error: "Premium subscription required" },
  },
  {
    status: 405,
    title: "Method not allowed",
    body: { ok: false, error: "Method not allowed" },
  },
  {
    status: 429,
    title: "API limit reached",
    body: {
      ok: false,
      error: "API limit reached",
      limit: "hourly",
      limit_max: API_RATE_LIMIT_HOURLY,
      used: API_RATE_LIMIT_HOURLY + 1,
      resets_at: "2026-05-23T09:00:00.000Z",
      message: "API limit reached — hourly",
    },
    headers: {
      "Retry-After": "1200",
      "X-RateLimit-Limit": String(API_RATE_LIMIT_HOURLY),
      "X-RateLimit-Remaining": "0",
    },
  },
];

export function getEndpointDoc(kind: ApiEndpointKind): ApiEndpointDoc {
  return (
    apiEndpointDocs.find((endpoint) => endpoint.kind === kind) ??
    apiEndpointDocs[0]
  );
}

export function buildApiRequestPath(
  endpoint: ApiEndpointKind,
  params: { from?: string; to?: string; limit?: string; offset?: string }
): string {
  const basePath = getEndpointDoc(endpoint).path;
  if (endpoint !== "transactions") return basePath;

  const searchParams = new URLSearchParams();
  if (params.from) searchParams.set("from", params.from);
  if (params.to) searchParams.set("to", params.to);
  if (params.limit) searchParams.set("limit", params.limit);
  if (params.offset) searchParams.set("offset", params.offset);

  const query = searchParams.toString();
  return query ? `${basePath}?${query}` : basePath;
}

export function docsContentAsMarkdown(lang: Language = "ENG"): string {
  const copy = apiDocsCopy[lang];
  const fieldsAsMarkdown = (fields: ApiFieldSpec[]) =>
    fields
      .map(
        (field) =>
          `- \`${field.name}\` (${field.type}): ${field.description[lang]}`
      )
      .join("\n");
  const assetTypeSections = apiAssetTypeDocs
    .map((assetType) => {
      const nestedSections = assetType.nestedFieldGroups?.length
        ? `\n\n${assetType.nestedFieldGroups
            .map(
              (group) => `#### \`${group.name}\` (${group.type})
${group.description[lang]}

${fieldsAsMarkdown(group.fields)}`
            )
            .join("\n\n")}`
        : "";

      return `### \`${assetType.form}\` — ${assetType.title[lang]}
${assetType.description[lang]}

${fieldsAsMarkdown(assetType.fields)}${nestedSections}`;
    })
    .join("\n\n");
  const endpointSections = apiEndpointDocs
    .map((endpoint) => {
      const params = endpoint.queryParams.length
        ? endpoint.queryParams
            .map(
              (param) =>
                `- \`${param.name}\` (${param.type}): ${param.description[lang]}`
            )
            .join("\n")
        : copy.noQueryParams;
      const fields = endpoint.responseFields
        .map(
          (field) =>
            `- \`${field.name}\` (${field.type}): ${field.description[lang]}`
        )
        .join("\n");
      const assetReference =
        endpoint.kind === "assets"
          ? `

### ${copy.commonAssetFieldsTitle}
${fieldsAsMarkdown(apiCommonAssetFields)}

### ${copy.assetTypesTitle}
${assetTypeSections}`
          : "";
      return `## ${endpoint.method} ${endpoint.path}

${endpoint.description[lang]}

${copy.useWhen}: ${endpoint.useWhen[lang]}

### ${copy.queryParams}
${params}

### ${copy.responseFields}
${fields}${assetReference}

### ${copy.exampleResponse}
\`\`\`json
${JSON.stringify(endpoint.exampleResponse, null, 2)}
\`\`\``;
    })
    .join("\n\n");

  return `# ${copy.productName}

## ${copy.nav.authentication}
${copy.authBody}

\`\`\`http
Authorization: Bearer <JWT_API_TOKEN>
\`\`\`

${copy.jwtBody}

### ${copy.tokenLifetimeTitle}
${copy.tokenLifetimeBody}

## ${copy.rateLimitTitle}
${copy.rateLimitBody}

${endpointSections}

## ${copy.errorsTitle}
${apiErrorExamples
  .map(
    (error) =>
      `- ${error.status} ${error.title}: \`${JSON.stringify(error.body)}\``
  )
  .join("\n")}

## ${copy.llmTitle}
${copy.llmBody}
`;
}

const OPEN_API_SCALAR_TYPES = [
  "string",
  "number",
  "boolean",
  "object",
  "array",
  "null",
];

/** Maps the doc-level type strings ("number | null", "ISO date string") to JSON Schema types. */
function toSchemaType(type: string): string | string[] {
  const mapped = type
    .split("|")
    .map((part) => part.trim().toLowerCase())
    .map((part) => (OPEN_API_SCALAR_TYPES.includes(part) ? part : "string"));
  return mapped.length === 1 ? mapped[0] : mapped;
}

function buildResponseSchema(endpoint: ApiEndpointDoc) {
  const required = endpoint.responseFields
    .filter((field) => field.required)
    .map((field) => field.name);
  return {
    type: "object",
    properties: Object.fromEntries(
      endpoint.responseFields.map((field) => [
        field.name,
        { type: toSchemaType(field.type), description: field.description.ENG },
      ])
    ),
    ...(required.length > 0 ? { required } : {}),
  };
}

function buildErrorResponse(status: number, description: string) {
  return {
    description,
    content: {
      "application/json": {
        schema: { $ref: "#/components/schemas/ApiError" },
        examples: Object.fromEntries(
          apiErrorExamples
            .filter((error) => error.status === status)
            .map((error) => [
              error.title.toLowerCase().replace(/[^a-z0-9]+/g, "_"),
              { summary: error.title, value: error.body },
            ])
        ),
      },
    },
  };
}

/** operationId is derived from the endpoint kind: assetWorth -> getAssetWorth. */
function toOperationId(kind: ApiEndpointKind): string {
  return `get${kind.charAt(0).toUpperCase()}${kind.slice(1)}`;
}

export function buildOpenApiDocument(apiBaseUrl: string = API_BASE_URL) {
  return {
    openapi: "3.1.0",
    info: {
      title: "Moneytor API",
      version: "1.0.0",
      description:
        "Read-only API for Moneytor asset worth, enriched assets, and cashflow transactions.",
    },
    servers: [{ url: apiBaseUrl }],
    components: {
      securitySchemes: {
        bearerAuth: {
          type: "http",
          scheme: "bearer",
          bearerFormat: "JWT",
          description: `JWT API tokens expire after ${API_TOKEN_TTL_MONTHS} months. Renew tokens in Moneytor API settings before expiry to keep integrations running.`,
        },
      },
      schemas: {
        ApiError: {
          type: "object",
          properties: {
            ok: { type: "boolean", const: false },
            error: {
              type: "string",
              description: "Human-readable error title.",
            },
            code: {
              type: "string",
              description: "Machine-readable error code, when available.",
            },
            message: {
              type: "string",
              description: "Extra detail about the error, when available.",
            },
          },
          required: ["ok", "error"],
        },
      },
    },
    security: [{ bearerAuth: [] }],
    paths: Object.fromEntries(
      apiEndpointDocs.map((endpoint) => [
        endpoint.path,
        {
          get: {
            operationId: toOperationId(endpoint.kind),
            summary: endpoint.summary.ENG,
            description: endpoint.description.ENG,
            parameters: endpoint.queryParams.map((param) => ({
              name: param.name,
              in: "query",
              required: false,
              schema: { type: param.type === "number" ? "number" : "string" },
              description: param.description.ENG,
            })),
            responses: {
              "200": {
                description: "Successful response",
                content: {
                  "application/json": {
                    schema: buildResponseSchema(endpoint),
                    example: endpoint.exampleResponse,
                  },
                },
              },
              "401": buildErrorResponse(
                401,
                "Missing, invalid, or expired API token"
              ),
              "403": buildErrorResponse(403, "Premium subscription required"),
              "429": buildErrorResponse(429, "Rate limit reached"),
            },
          },
        },
      ])
    ),
  };
}
